import { useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import StepPersonalInfo from "./StepPersonalInfo";
import StepReviewCredentials from "./StepReviewCredentials";
import StepProfilePhoto from "./StepProfilePhoto";
import StepSpecializations from "./StepSpecializations";
import StepVideoIntro from "./StepVideoIntro";
import StepSelfPayFee from "./StepSelfPayFee";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { storeUser } from "../../redux/users/usersSlice";
import moment from "moment-timezone";
import { toast } from "react-toastify";

const initialData = {
  firstName: "",
  lastName: "",
  credentials: [],
  yearsOfExperience: "",
  profilePhoto: null,
  specializations: [],
  videoIntro: null,
  fees: "",
  videoIntroUrl: "",
  time_zone: moment.tz.guess() || 'UTC',
  providerType: null,
};

const ProviderRegistration = () => {
  const user = useSelector((state) => state?.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);



  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialStep = parseInt(queryParams.get("step")) || 0;
  const [step, setStep] = useState(initialStep);

  const providerId = user.user?.providerId;
  const email = user.user?.email;

  // Prefill form data from backend if available
  useEffect(() => {
    const fetchProvider = async () => {
      if (!providerId) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/specialist/provider/${providerId}`
        );
        if (res.data.success && res.data.data) {
          const d = res.data.data;
          setFormData((prev) => ({
            ...prev,
            firstName: d.firstname || "",
            lastName: d.lastname || "",
            credentials: Array.isArray(d.credentials) ? d.credentials : [],
            yearsOfExperience: d.yearsOfExperience || "",
            profilePhoto: null,
            profilePhotoUrl: d.profilePhotoUrl || "",
            specializations: d.specialisation ? d.specialisation.split(",") : [],
            videoIntro: null,
            videoIntroUrl: d.videoIntroUrl || "",
            providerType: d.providerType || null,
          }));
        }
      } catch (err) {
        // ignore
      }
      setLoading(false);
    };
    fetchProvider();
    // eslint-disable-next-line
  }, [providerId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNextPersonalInfo = async (data, onNext) => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/register-provider`,
        {
          firstname: data.firstName,
          lastname: data.lastName,
          credentials: Array.isArray(data.credentials) ? JSON.stringify(data.credentials) : data.credentials,
          yearsOfExperience: data.yearsOfExperience,
          email: email,
          gender: data.gender,
          fees: data.fees,
          time_zone: formData.time_zone,
          providerType: data.providerType,
        }
      );
      onNext();
    } catch (err) {
      setError("Failed to save personal info. Please try again.");
    }
    setLoading(false);
  };

  const handleNextReviewCredentials = async (data, onNext) => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/register-provider`,
        {
          firstname: data.firstName,
          lastname: data.lastName,
          credentials: JSON.stringify(data.credentials),
          yearsOfExperience: data.yearsOfExperience,
          email: email,
          gender: data.gender,
          fees: data.fees,
          time_zone: formData.time_zone,
          providerType: data.providerType || formData.providerType,
        }
      );
      onNext();
    } catch (err) {
      setError("Failed to save credentials. Please try again.");
    }
    setLoading(false);
  };

  const handleNextProfilePhoto = async (data, onNext) => {
    setLoading(true);
    setError(null);
    try {
      const isTherapist = !data.providerType || data.providerType === 'Therapist';

      const form = new FormData();
      form.append("firstname", data.firstName);
      form.append("lastname", data.lastName);
      form.append("email", email);
      if (data.profilePhoto) {
        form.append("profilePhoto", data.profilePhoto);
      }
      form.append("time_zone", data.time_zone || 'UTC');
      form.append("providerType", data.providerType || 'Therapist');

      if (isTherapist) {
        form.append("credentials", Array.isArray(data.credentials) ? JSON.stringify(data.credentials) : data.credentials);
        form.append("yearsOfExperience", data.yearsOfExperience);
        form.append("gender", data.gender);
      } else {
        form.append("status", "Active");
      }

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/register-provider`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (!isTherapist) {
        setSuccess(true);
        setTimeout(() => {
          localStorage.clear();
          navigate("/login");
        }, 3000);
      } else {
        onNext();
      }
    } catch (err) {
      toast.error("Failed to save profile photo. Please try again.");
      setError("Failed to save profile photo. Please try again.");
    }
    setLoading(false);
  };

  const handleNextSpecializations = async (data, onNext) => {
    setLoading(true);
    setError(null);
    try {
      if (data.profilePhoto) {
        const form = new FormData();
        form.append("firstname", data.firstName);
        form.append("lastname", data.lastName);
        form.append("credentials", Array.isArray(data.credentials) ? JSON.stringify(data.credentials) : data.credentials);
        form.append("yearsOfExperience", data.yearsOfExperience);
        form.append("profilePhoto", data.profilePhoto);
        form.append("specialisation", data.specializations.join(","));
        form.append("email", email);
        form.append("gender", data.gender);

        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/specialist/register-provider`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/specialist/register-provider`,
          {
            firstname: data.firstName,
            lastname: data.lastName,
            credentials: Array.isArray(data.credentials) ? JSON.stringify(data.credentials) : data.credentials,
            yearsOfExperience: data.yearsOfExperience,
            specialisation: data.specializations.join(","),
            email: email,
            gender: data.gender,
          }
        );
      }

      onNext();
    } catch (err) {
      toast.error("Failed to save specializations. Please try again.");
      setError("Failed to save specializations. Please try again.");
    }
    setLoading(false);
  };

  const handleNextVideoIntro = async () => {
    if (!formData.videoIntro && !formData.videoIntroUrl) {
      toast.error("Please upload a video");
      setError("Please upload a video");
      return;
    }

    // If there's a new video file, upload it now
    if (formData.videoIntro) {
      setLoading(true);
      setError(null);
      try {
        const form = new FormData();
        form.append("firstname", formData.firstName);
        form.append("lastname", formData.lastName);
        form.append("credentials", Array.isArray(formData.credentials) ? JSON.stringify(formData.credentials) : formData.credentials);
        form.append("yearsOfExperience", formData.yearsOfExperience);
        form.append("profilePhoto", formData.profilePhoto);
        form.append("specialisation", formData.specializations.join(","));
        form.append("videoIntro", formData.videoIntro);
        form.append("email", email);
        form.append("gender", formData.gender);
        form.append("time_zone", formData.time_zone);

        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/specialist/register-provider`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (res.data.success && res.data.data?.videoIntroUrl) {
          setFormData(prev => ({ ...prev, videoIntroUrl: res.data.data.videoIntroUrl, videoIntro: null }));
        }
        handleNext();
      } catch (err) {
        toast.error("Failed to upload video. Please try again.");
        setError("Failed to upload video. Please try again.");
      }
      setLoading(false);
    } else {
      // If already uploaded and no new file, just move next
      handleNext();
    }
  };

  const handleSubmitSelfPayFee = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const form = new FormData();
      form.append("firstname", formData.firstName);
      form.append("lastname", formData.lastName);
      form.append("credentials", Array.isArray(formData.credentials) ? JSON.stringify(formData.credentials) : formData.credentials);
      form.append("yearsOfExperience", formData.yearsOfExperience);
      form.append("profilePhoto", formData.profilePhoto);
      form.append("specialisation", formData.specializations.join(","));
      form.append("videoIntro", formData.videoIntro);
      form.append("fees", formData.fees);
      form.append("email", email);
      form.append("gender", formData.gender);
      form.append("time_zone", formData.time_zone);

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/register-provider`,
        {
          firstname: formData.firstName,
          lastname: formData.lastName,
          credentials: Array.isArray(formData.credentials) ? JSON.stringify(formData.credentials) : formData.credentials,
          yearsOfExperience: formData.yearsOfExperience,
          specialisation: formData.specializations.join(","),
          fees: formData.fees,
          email: email,
          gender: formData.gender,
          time_zone: formData.time_zone,
          providerType: formData.providerType,
          status: 'Active',
        }
      );

      setSuccess(true);
      setTimeout(() => {
        localStorage.clear();
        navigate("/login");
      }, 3000);
    } catch (err) {
      toast.error("Failed to complete registration. Please try again.");
      setError("Failed to complete registration. Please try again.");
    }

    setLoading(false);
  };

  const handleNext = () => {
    setError(null);
    if (step === 0 && formData.providerType && formData.providerType !== "Therapist") {
      setStep(2);
    } else if (step === 2 && formData.providerType && formData.providerType !== "Therapist") {
      setStep(4);
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === 4 && formData.providerType && formData.providerType !== "Therapist") {
      setStep(2);
    } else if (step === 2 && formData.providerType && formData.providerType !== "Therapist") {
      setStep(0);
    } else {
      setStep((prevStep) => prevStep - 1);
    }
  };

  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#f8f8f8",
      }}
    >
      {step > 0 && (
        <div style={{ marginBottom: 16, width: "671px" }}>
          <button
            onClick={handleBack}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M29.3333 15.9998C29.3333 8.64784 23.3519 2.6665 15.9999 2.6665C8.64792 2.6665 2.66659 8.64784 2.66659 15.9998C2.66659 23.3518 8.64792 29.3332 15.9999 29.3332C23.3519 29.3332 29.3333 23.3518 29.3333 15.9998ZM10.1146 15.9998L17.7239 8.3905L19.6093 10.2758L13.8853 15.9998L19.6093 21.7238L17.7239 23.6092L10.1146 15.9998Z"
                fill="#27B99A"
              />
            </svg>
            <span style={{ color: "#27B99A", fontWeight: 600 }}>Back</span>
          </button>
        </div>
      )}
      <div
        style={{
          top: 134,
          left: 384,
          width: 671,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          background: "#fff",
          borderRadius: 12,
          borderLeft: "1px solid #E0E0E0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          padding: 40,
          zIndex: 10,
        }}
      >
        {step === 0 && (
          <StepPersonalInfo
            data={formData}
            onChange={handleChange}
            onNext={() => handleNextPersonalInfo(formData, handleNext)}
            loading={loading}
            error={error}
          />
        )}
        {step === 1 && (
          <StepReviewCredentials
            data={formData}
            onChange={handleChange}
            onNext={() => handleNextReviewCredentials(formData, handleNext)}
            loading={loading}
            error={error}
            onBack={handleBack}
          />
        )}
        {step === 2 && (
          <StepProfilePhoto
            data={formData}
            onChange={handleChange}
            onNext={() => handleNextProfilePhoto(formData, handleNext)}
            loading={loading}
            error={error}
          />
        )}
        {step === 3 && (
          <StepSpecializations
            data={formData}
            onChange={handleChange}
            onNext={() => handleNextSpecializations(formData, handleNext)}
            loading={loading}
            error={error}
            onBack={handleBack}
          />
        )}
        {step === 4 && (
          <StepVideoIntro
            data={formData}
            onChange={handleChange}
            onSubmit={handleNextVideoIntro}
            onBack={handleBack}
            loading={loading}
            error={error}
            success={success}
          />
        )}
        {step === 5 && (
          <StepSelfPayFee
            data={formData}
            onChange={handleChange}
            onNext={handleSubmitSelfPayFee}
            onBack={handleBack}
            loading={loading}
            error={error}
            success={success}
          />
        )}
        {success && (
          <div style={{ color: "green", marginTop: 16 }}>
            {user.user?.role === "careManager" ? "Care Manager" : "Provider"} registered successfully!
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderRegistration;
